"use client";

import Link from "next/link";
import { useAuth } from "@/lib/context/AuthProvider";
import { useLocale } from "@/lib/context/LocaleProvider";
import { Folder, Grid } from "@/components/ui/icons";

/** Calm, centered 404 — matches empty-state tone elsewhere in the app. */
export function NotFoundScreen() {
  const { t } = useLocale();
  const { isAuthenticated } = useAuth();

  return (
    <section className="animate-fade-up px-5 pt-7">
      <div className="relative mx-auto flex min-h-[58vh] max-w-[560px] flex-col items-center justify-center py-12 text-center">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-[-12%] top-1/2 h-[340px] -translate-y-1/2 rounded-[48px]"
          style={{
            background:
              "radial-gradient(ellipse at 50% 42%, color-mix(in srgb, var(--accent) 16%, transparent) 0%, transparent 70%)",
          }}
        />

        <div className="relative flex w-full flex-col items-center">
          <div
            aria-hidden="true"
            className="mb-2 select-none text-[72px] font-medium leading-none tracking-[-0.05em] text-accent/20 sm:mb-3 sm:text-[88px]"
          >
            404
          </div>
          <h1 className="mb-3 max-w-[420px] text-[32px] font-medium leading-[1.05] tracking-[-1.28px] text-ink">
            {t.notFoundTitle}
          </h1>
          <p className="mb-8 max-w-[400px] text-[15px] leading-[1.45] text-ink-3 [text-wrap:pretty]">
            {t.notFoundDesc}
          </p>

          <div className="flex flex-wrap items-center justify-center gap-2.5">
            <Link
              href="/tools"
              className="inline-flex h-[46px] items-center justify-center gap-[7px] rounded-full bg-accent px-6 text-[14px] font-semibold text-white transition hover:brightness-105"
            >
              <Grid size={16} />
              <span>{t.notFoundCtaTools}</span>
            </Link>
            {isAuthenticated ? (
              <Link
                href="/projects"
                className="inline-flex h-[46px] items-center justify-center gap-[7px] rounded-full border border-[rgba(26,24,22,0.16)] bg-white px-5 text-[14px] font-semibold text-ink transition-colors hover:bg-[rgba(26,24,22,0.04)]"
              >
                <Folder size={16} />
                <span>{t.notFoundCtaProjects}</span>
              </Link>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
}
